'use client';
import {
  useEffect, useRef, useState,
} from 'react';
import { useEffectOnce } from '@utils/react/hooks';
import '../Viewer.css';
import {
  TabList,
  VideoContainer,
  CLSPPlayer,
  DetailList,
  Button,
  Modal,
} from '@skyline-platform/alchemy';
import { StreamStepperFlow } from '@components/FormComponents/StreamStepperFlow/StreamStepperFlow';
import { tabEventEmitter } from '../utils/TabEventEmitters';
import { getStreams, deleteStream } from '@utils/graphql/creators/streams';
import { useMessageEmitter } from '@utils/react/eventEmitterContext';
import PropTypes from 'prop-types';
import { SourceForm } from '@components/FormComponents/SourceForm/SourceForm';
import { CopyButton } from '@components/CopyButton/CopyButton';
import {
  checkDuplicateTab, removeTab, streamAddressHasChanged, updateNestedField,
} from '../utils/helpers';
import { EditModal } from '@components/EditModal/EditModal';
import { DeletionModal } from '@components/DeletionModal/DeletionModal';
import { deleteSource } from '@utils/graphql/creators/sources';
import { sourceDeleteWarning } from '../utils/Constants';
import { StreamUrlsDisplay } from '@components/StreamUrlsDisplay/StreamUrlsDisplay';
import { formatSourceData } from '@components/FormComponents/SourceForm/utils';
import { getLogger, filterEmptyValues } from '@utils/general';

const logger = getLogger('src.pages.split.streams.StreamViewer');

const getCopyIcon = (id) => {
  return <CopyButton id={id} />;
};

export const StreamViewer = ({ onTabDataPopulate, userIsAdmin }) => {
  const [currentFocusedTabId, setCurrentFocusedTabId] = useState(null);
  const [tabData, setTabData] = useState([]);
  const [focusTabId, setFocusTabId] = useState(null);
  const tabDataRef = useRef(tabData);
  const [title, setTitle] = useState('');

  const retrieveStreamById = async (streamId) => {
    const { query, execute } = getStreams(streamId);
    query.addReturnProperties(
      'id',
      'name',
      'description',
      'clsps_address',
      'pull_on_request',
      'use_interleaved_rtsp_transport',
      'snapshot_interval',

      'appliance.id',
      'appliance.name',
      'appliance.management_address',
      'appliance.public_address',

      'source.id',
      'source.name',
      'source.description',
      'source.category',
      'source.sdp_input',
      'source.path',
      'source.port',
      'source.uri',
      'source.scheme',
      'source.device.id',
      'source.device.name',
      'source.device.network_address',
      'source.appliance.id',
      'source.appliance.name',
      'source.appliance.management_address',
      'source.streams.id',
      'source.streams.name',

      'zone.id',
      'zone.name'
    );

    try {
      const { error, data } = await execute();
      if (error) {
        logger.error(error);
        return null;
      }
      return data;
    } catch (e) {
      logger.warn(e);
      return null;
    }
  };

  /**
   * Triggered by the on onClick on AllStreams table.
   * Gets the stream by id and checks to see if it is already an open tab
   * @param {*} eventObject - Contains the event information of the clicked item in the table
   */
  const handleAddTabs = async (eventObject) => {
    const streamId = eventObject.detail;
    const newStream = await retrieveStreamById(streamId);

    if (newStream) {
      const tabResult = checkDuplicateTab(newStream, setTabData, setFocusTabId);
      if (tabDataRef.current.length > 0) {
        // Focus on the correct tab based on whether it's new or existing
        const targetTabId = `tab-${tabResult.tabIndex}`;
        const targetTab = document.getElementById(targetTabId);
        const tableNameTag = document.getElementById(`datacell-${streamId}`);

        if (targetTab) {
          targetTab.click();
        }
        if (tableNameTag) {
          tableNameTag.focus(); // stop focus from jumping over to tabs
        }
        setCurrentFocusedTabId(streamId);
      }
    }
  };

  // replace current stream record with updated one from database
  const updateStream = async (streamId) => {
    const updatedStream = await retrieveStreamById(streamId);

    if (updatedStream) {
      setTabData((prevTabs) => {
        return prevTabs.map((stream) => {
          if (stream.id !== updatedStream.id) {
            return stream;
          }
          // clone the current stream data because all properties are read-only
          // note: structuredClone is available on modern browsers as of 2022. if we are supporting
          // browsers from before this then a polyfill is available in the core-js package.
          const clone = structuredClone(updatedStream);
          // if the stream address has not changed
          if (!streamAddressHasChanged(stream, clone)) {
            clone.clsps_address = stream.clsps_address; // reassign the clsp address to the original
          }
          return clone;
        });
      });
    }
  };

  const handleUpdatingStream = (updateMessage) => {
    if (tabDataRef.current.length === 0) {
      return; // Do nothing if there are currently no tabs
    }

    // Do nothing if there is no updated data information in the message
    if (!updateMessage.detail.payload) {
      return;
    }

    const {
      model = '',
      action = '',
      data: jsonData = '',
    } = updateMessage.detail.payload;
    const data = JSON.parse(jsonData);

    const propertyToUpdate = model.toLowerCase();

    switch (action.toLowerCase()) {
      case 'create': // do nothing on create
        return;
      case 'remove':
        if (propertyToUpdate === 'stream') {
          // delete stream from tab array

          const index = tabDataRef.current.findIndex((tab) => {
            return String(tab.id) === String(data.previous.id);
          });

          removeTab(index, tabDataRef.current, setTabData, tabEventEmitter, setCurrentFocusedTabId);
        }
        return;
      case 'update':
        switch (propertyToUpdate) {
          case 'stream': // find updated stream by id and update it in tab array
            if (tabDataRef.current.length && tabDataRef.current.some((tab) => tab.id === data.current.id)) {
              updateStream(data.current.id);
            }
            return;
          case 'appliance': {
            // Guard: only patch if a display field changed — not connection_count.
            // During a storm, hundreds of appliance events arrive per minute but
            // total_connection_count is not shown in the stream viewer.
            const APPLIANCE_DISPLAY_FIELDS = [
              'name',
              'management_address',
              'public_address',
            ];
            const hasRelevantChange = APPLIANCE_DISPLAY_FIELDS.some(
              (field) => data.current[field] !== data.previous[field]
            );
            if (!hasRelevantChange) return;
            updateNestedField(propertyToUpdate, data.current, tabDataRef)
              .then((updatedArray) => setTabData(updatedArray));
            return;
          }
          case 'zone':
          case 'source':
            updateNestedField(propertyToUpdate, data.current, tabDataRef)
              .then((updatedArray) => setTabData(updatedArray));
            return;
        }
    }
  };

  /***** SUBSCRIPTIONS *****/
  const messageHandler = useMessageEmitter();
  useEffectOnce(() => {
    messageHandler.subscribe(handleUpdatingStream);

    return () => {
      messageHandler.unsubscribe(handleUpdatingStream);
    };
  });
  /***** END SUBSCRIPTIONS *****/

  // Handles receiving tabs from AllStreams component
  useEffect(() => {
    // Listen for updateTabs events
    tabEventEmitter.on('updateTabs', handleAddTabs);

    onTabDataPopulate();
    tabDataRef.previous = tabDataRef.current;

    tabDataRef.current = tabData;

    // Clean up the event listener on component unmount
    return () => {
      tabEventEmitter.off('updateTabs', handleAddTabs);
    };
  }, [tabData]);

  /**
   * Used when deleting a tab in the tablist that comes before the one
   * currently focused/selected. It has to be in the use effect to maintain
   * focus on the tab after the rerender when tabData updates.
   */
  useEffect(() => {
    if (currentFocusedTabId) {
      const currentFocusedTab =
        document.getElementById(currentFocusedTabId)?.parentElement;
      if (currentFocusedTab) {
        currentFocusedTab.focus();
        currentFocusedTab.click();
      }
    }
  }, [tabData, currentFocusedTabId]);

  useEffect(() => {
    if (focusTabId !== null) {
      const tabElement = document.getElementById(focusTabId)?.parentElement;
      if (tabElement) {
        tabElement.focus();
        tabElement.click();
      }
      setTimeout(() => setFocusTabId(null), 0); // Reset the focusTabId after handling
    }
  }, [focusTabId]);

  return (
    <>
      {tabData && tabData.length > 0 ? ( //don't render if no current tabs
        <TabList
          canClose
          handleClose={(index) => removeTab(index, tabData, setTabData, tabEventEmitter, setCurrentFocusedTabId)}
          pages={tabData.map((tab, index) => {
            const streamDetails = {
              Name: tab.name,
              Description: tab.description,
              ['On Demand']: tab.pull_on_request ? 'On' : 'Off',
              ['RTSP Interleaved']: tab.use_interleaved_rtsp_transport
                ? 'On'
                : 'Off',
              ...(tab.snapshot_interval
                ? { 'Snapshot Interval': `${tab.snapshot_interval} seconds` }
                : {}),
              'Stream Profile': (
                <>
                  <span className='label' title={tab?.source?.name}>{tab?.source?.name}</span>
                  <div>
                    <span id='source-id-label'>| ID: </span>
                    <span id='source-id'>{tab?.source?.id}</span>
                  </div>
                  {getCopyIcon('source-id')}
                </>
              ),
            };

            const sourceInfo = formatSourceData(tab.source);
            const sourceDetails = {
              Name: sourceInfo.name,
              Description: sourceInfo.description,
              Category: sourceInfo.category,
              Type: sourceInfo.type,
              [sourceInfo.type === 'Camera' ? 'Camera' : 'Appliance']:
                sourceInfo.type === 'Camera'
                  ? sourceInfo?.device?.name
                  : sourceInfo?.appliance?.name,
              'Input Type': sourceInfo.inputType,
              ...(sourceInfo.inputType === 'URI' && {
                Protocol: sourceInfo.protocol,
                Path: sourceInfo.path,
                Port: sourceInfo.port,
              }),
              ...(sourceInfo.inputType === 'SDP' && {
                'SDP Input': sourceInfo.sdpInput,
              }),
            };

            if (tab.source?.device?.name && tab.source?.device?.id) {
              streamDetails['Camera'] = (
                <>
                  <a
                    href={`/cameras?id=${encodeURIComponent(tab.source?.device.id)}`}
                    title={tab.source?.device.name}
                  >
                    {tab.source?.device.name}
                  </a>
                  <div>
                    <span id='source-device-id-label'>| ID: </span>
                    <span id='source-device-id'>{tab.source?.device.id}</span>
                  </div>
                  {getCopyIcon('source-device-id')}
                </>
              );
            } else {
              streamDetails['Appliance'] = (
                <>
                  <span className='label' title={tab.source?.appliance?.name}>{tab.source?.appliance?.name}</span>
                  <div>
                    <span id='source-appliance-id-label'>| ID: </span>
                    <span id='source-appliance-id'>
                      {tab.source?.appliance?.id}
                    </span>
                  </div>
                  {getCopyIcon('source-appliance-id')}
                </>
              );
            }
            // the following two properties are down here to make them show below Camera/Appliance
            // @todo: potentially update the DetailList component to allow ordering with a prop
            streamDetails['Zone'] = (
              <>
                <span className='label' title={tab?.zone?.name}>{tab?.zone?.name}</span>
                <div>
                  <span id='zone-id-label'>| ID: </span>
                  <span id='zone-id'>{tab?.zone?.id}</span>
                </div>
                {getCopyIcon('zone-id')}
              </>
            );
            streamDetails['Host Appliance'] = (
              <>
                <span className='label' title={tab?.appliance?.name}>{tab?.appliance?.name}</span>
                <div>
                  <span id='host-appliance-id-label'>| ID: </span>
                  <span id='host-appliance-id'>{tab?.appliance?.id}</span>
                </div>
                {getCopyIcon('host-appliance-id')}
              </>
            );
            return (
              <>
                <VideoContainer
                  title={
                    `Zone: ${tab.zone?.name} | Address: ${tab.appliance?.public_address ||
                    tab.appliance?.management_address
                    }`}
                  key={tab.id}
                  containerColor='var(--vp-skyline-blue)'
                >
                  <CLSPPlayer
                    title={tab.name ?? ''}
                    type='clsp'
                    src={tab?.clsps_address}
                    key={tab?.id}
                  />
                </VideoContainer>

                <TabList
                  pages={[
                    <div key={'detail' + index}>
                      {/* showing scrollbars */}
                      <DetailList keyValuePair={filterEmptyValues(streamDetails)} tabIndex={0} />
                      {userIsAdmin && (
                        <>
                          <div className='button-container'>
                            <Modal
                              className='edit-stream-modal'
                              closeIcon
                              header={title}
                              disableBackdropClick
                              trigger={({ handleOpen }) => (
                                <Button
                                  key={'edit-button' + index}
                                  label='Edit Stream Details'
                                  type='filled'
                                  onClick={handleOpen}
                                />
                              )}
                              slots={{
                                content: (handleClose) => (
                                  <StreamStepperFlow
                                    handleClose={handleClose}
                                    streamData={tab.id}
                                    setTitle={setTitle}
                                  />
                                ),
                              }}
                            />
                            <DeletionModal
                              data={tab}
                              handleDelete={deleteStream}
                              fieldName='stream'
                              trigger={({ handleOpen }) =>
                                <Button
                                  key={'delete-button'}
                                  label='Delete Stream'
                                  type='filled'
                                  state='error'
                                  onClick={handleOpen}
                                />
                              }
                            />
                          </div>
                        </>
                      )}
                    </div>,
                    <div key={'source' + index}>
                      <DetailList keyValuePair={filterEmptyValues(sourceDetails)} />
                      {userIsAdmin && (
                        <>
                          <div className='button-container'>
                            <EditModal
                              data={sourceInfo}
                              FormComponent={SourceForm}
                              setTitle={setTitle}
                              fieldName='streamProfile'
                              trigger={({ handleOpen }) =>
                                <Button
                                  key={'edit-button' + index}
                                  label='Edit Stream Profile Details'
                                  type='filled'
                                  onClick={handleOpen}
                                />
                              }
                            />
                            <DeletionModal
                              customMessage={sourceDeleteWarning}
                              headerIcon='fa-solid fa-layer-group'
                              data={sourceInfo}
                              handleDelete={deleteSource}
                              fieldName='streamProfile'
                              trigger={({ handleOpen }) =>
                                <Button
                                  key={'delete-button' + index}
                                  label='Delete Stream Profile'
                                  state='error'
                                  type='filled'
                                  onClick={handleOpen}
                                />
                              }
                            />
                          </div>
                        </>
                      )}
                    </div>,
                    ...(userIsAdmin ? [<div key={'urls' + index}><StreamUrlsDisplay streamId={tab.id} /></div>] : []),
                  ]}
                  tabs={[
                    'Details',
                    'Stream Profile',
                    ...(userIsAdmin ? ['URLs'] : []),
                  ]}
                />
              </>
            );
          })}
          tabs={tabData.map((tab, index) => (
            <span
              title={tab.name}
              key={index}
              id={tab.id}
              tabIndex={-1}
              onClick={() => {
                tabEventEmitter.emit('selectTab', tab.id); //emits event to AllStreams
                // This is needed to keep the correct tab focused when making an edit to data in that tab
                setCurrentFocusedTabId(tab.id);
              }}
            >
              {tab.name}
            </span>
          ))}
        />
      ) : null}
    </>
  );

};


StreamViewer.propTypes = {
  onTabDataPopulate: PropTypes.func.isRequired,
  userIsAdmin: PropTypes.bool.isRequired,
};
